import { routeToTab } from '@/utils/tabs'

const MAX_TABS = 10

const state = {
  visitedTabs: [],
  activeKey: ''
}

function createRefreshKey() {
  return Date.now()
}

function pruneLRU(state) {
  if (state.visitedTabs.length <= MAX_TABS) return
  let oldestIdx = -1
  let oldestTime = Infinity
  state.visitedTabs.forEach((t, i) => {
    if (t.affix) return
    if (t.key === state.activeKey) return
    if ((t.lastActive || 0) < oldestTime) {
      oldestTime = t.lastActive || 0
      oldestIdx = i
    }
  })
  if (oldestIdx !== -1) {
    state.visitedTabs.splice(oldestIdx, 1)
  }
}

const getters = {
  visitedTabs: (s) => s.visitedTabs,
  activeKey: (s) => s.activeKey,
  cachedNames: (s) =>
    s.visitedTabs.filter((t) => t.keepAlive && t.name).map((t) => t.name),
  currentRefreshKey: (s) => {
    const tab = s.visitedTabs.find((t) => t.key === s.activeKey)
    return tab?.refreshKey || 0
  }
}

const mutations = {
  ADD_TAB(state, tab) {
    const idx = state.visitedTabs.findIndex((t) => t.key === tab.key)
    if (idx === -1) {
      state.visitedTabs.push({ ...tab, refreshKey: createRefreshKey() })
      pruneLRU(state)
    } else {
      state.visitedTabs[idx] = {
        ...state.visitedTabs[idx],
        ...tab,
        refreshKey: state.visitedTabs[idx].refreshKey || 0,
        lastActive: Date.now()
      }
    }
  },
  SET_ACTIVE(state, key) {
    state.activeKey = key
    const tab = state.visitedTabs.find((t) => t.key === key)
    if (tab) tab.lastActive = Date.now()
  },
  REMOVE_TAB(state, key) {
    const idx = state.visitedTabs.findIndex((t) => t.key === key)
    if (idx === -1) return
    if (state.visitedTabs[idx].affix) return
    state.visitedTabs.splice(idx, 1)
    if (state.activeKey === key) {
      state.activeKey = ''
    }
  },
  CLOSE_OTHERS(state, key) {
    state.visitedTabs = state.visitedTabs.filter(
      (t) => t.affix || t.key === key
    )
  },
  CLOSE_ALL(state) {
    state.visitedTabs = state.visitedTabs.filter((t) => t.affix)
    if (!state.visitedTabs.find((t) => t.key === state.activeKey)) {
      state.activeKey = ''
    }
  },
  BUMP_REFRESH(state, key) {
    const tab = state.visitedTabs.find((t) => t.key === key)
    if (tab) tab.refreshKey = (tab.refreshKey || 0) + 1
  },
  HYDRATE(state, payload) {
    const stored = Array.isArray(payload?.visitedTabs)
      ? payload.visitedTabs
      : []
    const currentKey = state.activeKey
    const currentTab = state.visitedTabs.find((t) => t.key === currentKey)
    state.visitedTabs = stored.map((t) => ({
      refreshKey: 0,
      ...t
    }))
    if (currentTab && !state.visitedTabs.find((t) => t.key === currentKey)) {
      state.visitedTabs.push(currentTab)
    }
    if (!state.visitedTabs.find((t) => t.key === state.activeKey)) {
      state.activeKey = payload?.activeKey || ''
    }
  },
  CLEAR(state) {
    state.visitedTabs = []
    state.activeKey = ''
  }
}

const actions = {
  addTabFromRoute({ commit }, route) {
    const tab = routeToTab(route)
    commit('ADD_TAB', tab)
    commit('SET_ACTIVE', tab.key)
  },
  seedAffixTabs({ commit, state }, routes) {
    routes.forEach((r) => {
      if (!r.meta?.affix) return
      if (state.visitedTabs.find((t) => t.key === r.path)) return
      commit('ADD_TAB', {
        key: r.path,
        title: r.meta?.title || r.name || r.path,
        name: r.name,
        path: r.path,
        fullPath: r.path,
        affix: true,
        keepAlive: r.meta?.keepAlive !== false,
        lastActive: 0
      })
    })
  },
  removeTab({ commit }, key) {
    commit('REMOVE_TAB', key)
  },
  closeOthers({ commit }, key) {
    commit('CLOSE_OTHERS', key)
  },
  closeAll({ commit }) {
    commit('CLOSE_ALL')
  },
  refreshTab({ commit, state }, key) {
    const target = key || state.activeKey
    if (!target) return
    commit('BUMP_REFRESH', target)
  },
  clearAll({ commit }) {
    commit('CLEAR')
  },
  hydrate({ commit }, payload) {
    commit('HYDRATE', payload)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
